import _ from 'lodash'
/**
 * CUSTOM DIMENSION indexes as created in google analytics
 */
const INDEX_SCHOOL_CODE = 1
const INDEX_USER_PROFILE = 2
const INDEX_SUBJECT = 3
const INDEX_YEAR = 4
const INDEX_SKILL = 5
const INDEX_CONTENT_STANDARD = 6
const INDEX_CURRICULUM_VERSION = 7
const INDEX_SYLLABUS = 8
const INDEX_UNIT = 9

const sendGtag = (...args) => {
  if (window.gtag) {
    // console.log("Sending GA with event %O", args)
    window.gtag(...args)
  } else {
    console.info("Did not send gtag as it was not available - you should not see this in production %O", ...args)
  }
}

/**
 * @return {String?} Object.dimension1 The school code, if available from the window
 * @return {String?} Object.dimension2 The user type (One of "Student" or "Staff", if available from the window
 */
const getUserParams = () => {
  var params = {}
  if (window.__USER) {
    if (window.__USER.school_code) {
      params[`dimension${INDEX_SCHOOL_CODE}`] = window.__USER.school_code
    }
    if (window.__USER.user_type) {
      params[`dimension${INDEX_USER_PROFILE}`] = window.__USER.user_type
    }
  }
  return params
}

/**
 *
 * @param {Array<string>} Object.subjects An array of subject names
 * @param {Array<string>} Object.years An array of year names
 * @param {Array<string>} Object.learningSkills An array of learning skill names
 * @param {Array<string>} Object.contentStandards An array of content standard names
 * @return {String} Object.subject A semicolon-separated string of subjects
 * @return {String} Object.year A semicolon-separated string of years
 * @return {String} Object.skill-theme A semicolon-separated string of learning skills
 * @return {String} Object.content-standard A semicolon-separated string of content standards
 */
const eventLabelsFromCurriculum = ({
  subjects,
  years,
  learningSkills,
  contentStandards
}) => {
  var labels = {}
  if (subjects && subjects.length)
    labels.subject = subjects.join('; ')
  if (years && years.length)
    labels.year = years.join('; ')
  if (learningSkills && learningSkills.length)
    labels['skill-theme'] = learningSkills.join('; ')
  if (contentStandards && contentStandards.length)
    labels['content-standard'] = contentStandards.join('; ')
  return labels
}

/**
 *
 * @param {Array<string>} Object.subjects An array of subject names
 * @param {Array<string>} Object.years An array of year names
 * @param {Array<string>} Object.learningSkills An array of learning skill names
 * @param {Array<string>} Object.contentStandards An array of content standard names
 * @return {String?} Object.dimension3 A semicolon-separated string of subjects
 * @return {String?} Object.dimension4 A semicolon-separated string of years
 * @return {String?} Object.dimension5 A semicolon-separated string of learning skills
 * @return {String?} Object.dimension6 A semicolon-separated string of content standards
 */
const dimensionsFromCurriculum = ({
  subjects,
  years,
  learningSkills,
  contentStandards,
  curriculumVersions,
  syllabuses,
  units
}) => {
  var dimensions = {}
  if (subjects)
    dimensions[`dimension${INDEX_SUBJECT}`] = subjects.join('; ')
  if (years)
    dimensions[`dimension${INDEX_YEAR}`] = years.join(';')
  if (learningSkills)
    dimensions[`dimension${INDEX_SKILL}`] = learningSkills.join('; ')
  if (contentStandards)
    dimensions[`dimension${INDEX_CONTENT_STANDARD}`] = contentStandards.join('; ')
  if (curriculumVersions)
    dimensions[`dimension${INDEX_CURRICULUM_VERSION}`] = curriculumVersions.join(';')
  if (syllabuses)
    dimensions[`dimension${INDEX_SYLLABUS}`] = syllabuses.join(';')
  if (units)
    dimensions[`dimension${INDEX_UNIT}`] = units.join(';')
  return dimensions
}

/**
 *
 * @param {Array<Object>} tags An array of tags
 * @return {Array<string>} Object.subjects An array of subject names
 * @return {Array<string>} Object.years An array of year names
 * @return {Array<string>} Object.learningSkills An array of learning skill names
 * @return {Array<string>} Object.contentStandards An array of content standard names
 */
const curriculumArgsFromTags = (tags) => {
  return tags.reduce((args, tag) => {
    if (tag.type == 'subject')
      args.subjects.push(tag.value)
    if (tag.type == 'year')
      args.years.push(tag.value)
    if (tag.type == 'learning_skill')
      args.learningSkills.push(tag.value)
    if (tag.type == 'content_standard')
      args.contentStandards.push(tag.value)
    return args
  }, {
    subjects: [],
    years: [],
    learningSkills: [],
    contentStandards: [],
  })
}

export default {
 /**
 * Sends a google tag when curriculums are filtered
 * @param {Array<Object>} tags An array of tags
 * @param {Array<string>} Object.subjects An array of subject names
 * @param {Array<string>} Object.years An array of year names
 * @param {Array<string>} Object.learningSkills An array of learning skill names
 * @param {Array<string>} Object.contentStandards An array of content standard names
 */
  sendCurriculumFilterEvent: (curriculumArgs) => {
    const params = {
      'event_category': 'Curriculum Filtering',
      ...getUserParams(),
      ...dimensionsFromCurriculum(curriculumArgs),
      'event_label': JSON.stringify(eventLabelsFromCurriculum(curriculumArgs))
    }
    sendGtag('event', 'User Interaction', params)
  },

/**
 * Sends a google tag when a site is added
 * @param {Site} site A site
 * @param {Array<string>} tags An array of tags
 */
  sendSiteAddEvent: (site, tags) => {
    const curriculumArgs = curriculumArgsFromTags(tags)
    const eventLabels = {
      ...eventLabelsFromCurriculum(curriculumArgs),
      site_id: site.frogstore_site_uuid,
      site_title: site.title
    }
    const params = {
      'event_category': 'Site Used',
      ...getUserParams(),
      ...dimensionsFromCurriculum(curriculumArgs),
      'event_label': JSON.stringify(eventLabels)
    }
    sendGtag('event', 'Item Download', params)
  },
/**
 * Sends a google tag when a site is previewed
 * @param {Site} site A site
 * @param {Array<string>} tags An array of tags
 */
  sendSitePreviewEvent: (site, tags) => {
    const curriculumArgs = curriculumArgsFromTags(tags)
    const eventLabels = {
      ...eventLabelsFromCurriculum(curriculumArgs),
      site_id: site.frogstore_site_uuid,
      site_title: site.title
    }
    const params = {
      'event_category': 'Site Previewed',
      ...getUserParams(),
      ...dimensionsFromCurriculum(curriculumArgs),
      'event_label': JSON.stringify(eventLabels)
    }
    sendGtag('event', 'User Interaction', params)
  }
}